/**
 * The controls for a list's filters, drawn from `FilterDef`s.
 *
 * Shared by the filter drawer beside a list and by step 2 of the Create View
 * dialog (INV-016, SET-030), so a view can only say what the drawer can.
 * Values are keyed as the drawer keys them: a date range as `<key>:from` and
 * `<key>:to`.
 */
import { DOCUMENT_STATUS, type FilterDef } from '@project/contracts'
import { Control } from './controls.js'

export type FilterValues = Record<string, string>

const STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Draft',
  IN_REVIEW: 'In review',
  APPROVED: 'Approved',
  CANCELLED: 'Cancelled',
}

const filterField = (f: FilterDef) => ({
  key: f.key,
  label: f.label,
  control: f.control === 'status' ? ('select' as const) : f.control === 'dateRange' ? ('date' as const) : f.control,
  options: f.control === 'status' ? DOCUMENT_STATUS.map((s) => ({ value: s, label: STATUS_LABELS[s] ?? s })) : f.options,
  lookup: f.lookup,
})

export const FilterFields = ({
  filters, values, onChange,
}: {
  filters: readonly FilterDef[]
  values: FilterValues
  onChange: (key: string, value: string) => void
}) => (
  <div className="stack" style={{ marginTop: 18 }}>
    {filters.map((f) => {
      const base = filterField(f)
      if (f.control === 'dateRange') {
        return (
          <div key={f.key} className="stack" style={{ gap: 8 }}>
            <span className="label">{f.label}</span>
            <Control
              field={{ ...base, key: `${f.key}:from`, label: `${f.label} from` }}
              value={values[`${f.key}:from`] ?? ''}
              onChange={(v) => onChange(`${f.key}:from`, typeof v === 'string' ? v : '')}
            />
            <Control
              field={{ ...base, key: `${f.key}:to`, label: `${f.label} to` }}
              value={values[`${f.key}:to`] ?? ''}
              onChange={(v) => onChange(`${f.key}:to`, typeof v === 'string' ? v : '')}
            />
          </div>
        )
      }
      return (
        <div key={f.key} className="stack" style={{ gap: 8 }}>
          <span className="label">{f.label}</span>
          <Control
            field={base}
            value={values[f.key] ?? ''}
            onChange={(v) => onChange(f.key, typeof v === 'string' ? v : '')}
          />
        </div>
      )
    })}
  </div>
)
